import { useEffect, useState, useRef, useCallback } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { Star, ChevronLeft, ChevronRight, ArrowUpRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function StarRow({ n = 5 }) {
  return (
    <div className="flex gap-1 text-[#D97645]" aria-label={`${n} out of 5 stars`}>
      {Array.from({ length: 5 }).map((_, i) => (
        <Star key={i} size={16} fill={i < n ? "currentColor" : "none"} strokeWidth={1.5} />
      ))}
    </div>
  );
}

function Initial({ name }) {
  const ch = (name || "G").trim().charAt(0).toUpperCase();
  return (
    <div className="w-11 h-11 shrink-0 rounded-full bg-[#3A3229] border border-[#D97645]/40 flex items-center justify-center font-display text-lg text-[#F2E7D0]">
      {ch}
    </div>
  );
}

/**
 * Live Google reviews carousel.
 * - variant="full"  → large section for Home page (auto-play every 6s, arrows + dots)
 * - variant="strip" → compact single-line quote for the Booking page (auto-play every 5s)
 */
export default function GoogleReviewsCarousel({ variant = "full" }) {
  const [state, setState] = useState({ reviews: [], rating_avg: 0, rating_count: 0, google_maps_uri: "" });
  const [i, setI] = useState(0);
  const [hover, setHover] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/reviews`)
      .then(({ data }) => setState({
        reviews: data.reviews || [],
        rating_avg: data.rating_avg || 0,
        rating_count: data.rating_count || 0,
        google_maps_uri: data.google_maps_uri || "",
      }))
      .catch(() => {});
  }, []);

  const total = state.reviews.length;
  const next = useCallback(() => setI((v) => (total ? (v + 1) % total : 0)), [total]);
  const prev = useCallback(() => setI((v) => (total ? (v - 1 + total) % total : 0)), [total]);

  useEffect(() => {
    if (!total || hover) return;
    clearInterval(timerRef.current);
    timerRef.current = setInterval(next, variant === "strip" ? 5000 : 6000);
    return () => clearInterval(timerRef.current);
  }, [next, total, hover, variant]);

  if (!total) return null;
  const r = state.reviews[i];
  const mapsUrl = state.google_maps_uri || "https://www.google.com/maps/search/?api=1&query=Tikratoli+Farm+Resorts+Ranchi";

  // -------- STRIP (Booking page) --------
  if (variant === "strip") {
    return (
      <div
        data-testid="reviews-strip"
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        className="border border-[#3A3229] bg-[#27221C]/60 px-5 py-4 flex items-start gap-4"
      >
        <div className="flex flex-col items-center gap-1 shrink-0 pr-4 border-r border-[#3A3229]">
          <div className="font-display text-3xl text-[#D97645] leading-none">
            {state.rating_avg ? state.rating_avg.toFixed(1) : "5.0"}
          </div>
          <StarRow n={5} />
          <div className="font-mono-label text-[10px] text-[#B8A891] mt-1">Google</div>
        </div>
        <div className="min-w-0 flex-1">
          <AnimatePresence mode="wait">
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -6 }}
              transition={{ duration: 0.5 }}
            >
              <p className="text-[#F2E7D0] text-sm italic leading-snug line-clamp-2">"{r.text}"</p>
              <p className="mt-1 font-mono-label text-[11px] text-[#B8A891] truncate">
                — {r.author}{r.when ? ` · ${r.when}` : ""}
              </p>
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    );
  }

  // -------- FULL (Home page) --------
  return (
    <div
      data-testid="reviews-carousel"
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      className="relative"
    >
      {/* Rating header */}
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div className="flex items-center gap-4">
          <div className="font-display text-6xl text-[#D97645] leading-none">
            {state.rating_avg ? state.rating_avg.toFixed(1) : "5.0"}
          </div>
          <div>
            <StarRow n={Math.round(state.rating_avg) || 5} />
            <div className="mt-1 font-mono-label text-xs text-[#B8A891]">
              {state.rating_count > 0
                ? `Rated by ${state.rating_count.toLocaleString("en-IN")} guests on Google`
                : "Rated on Google · updated monthly"}
            </div>
          </div>
        </div>
        <a
          href={mapsUrl}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="reviews-google-link"
          className="inline-flex items-center gap-2 font-mono-label text-[#D97645] link-underline"
        >
          Read all reviews on Google <ArrowUpRight size={14} />
        </a>
      </div>

      {/* Carousel card */}
      <div className="relative border border-[#3A3229] bg-[#27221C] p-8 md:p-12 min-h-[320px] md:min-h-[280px] flex flex-col justify-between overflow-hidden">
        <AnimatePresence mode="wait">
          <motion.div
            key={i}
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -24 }}
            transition={{ duration: 0.7, ease: [0.25, 0.1, 0.25, 1] }}
          >
            <StarRow n={r.rating || 5} />
            <p className="mt-6 font-display italic text-2xl md:text-3xl text-[#F2E7D0] leading-snug">
              "{r.text}"
            </p>
          </motion.div>
        </AnimatePresence>
        <AnimatePresence mode="wait">
          <motion.div
            key={`meta-${i}`}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5, delay: 0.15 }}
            className="mt-8 flex items-center gap-3"
          >
            {r.photo ? (
              <img src={r.photo} alt={r.author} className="w-11 h-11 rounded-full object-cover border border-[#D97645]/40" />
            ) : (
              <Initial name={r.author} />
            )}
            <div>
              <div className="font-display text-lg text-[#F2E7D0]">{r.author}</div>
              <div className="font-mono-label text-[11px] text-[#B8A891]">
                {r.when ? r.when + " · " : ""}via Google
              </div>
            </div>
          </motion.div>
        </AnimatePresence>

        {/* Nav controls */}
        <div className="absolute bottom-6 right-6 flex items-center gap-2">
          <button
            type="button"
            data-testid="reviews-prev"
            onClick={prev}
            aria-label="Previous review"
            className="w-10 h-10 border border-[#3A3229] hover:border-[#D97645] text-[#F2E7D0] flex items-center justify-center transition"
          >
            <ChevronLeft size={16} />
          </button>
          <button
            type="button"
            data-testid="reviews-next"
            onClick={next}
            aria-label="Next review"
            className="w-10 h-10 border border-[#3A3229] hover:border-[#D97645] text-[#F2E7D0] flex items-center justify-center transition"
          >
            <ChevronRight size={16} />
          </button>
        </div>
      </div>

      {/* Dots */}
      <div className="mt-5 flex items-center gap-2" role="tablist" aria-label="Review slides">
        {state.reviews.map((_, idx) => (
          <button
            key={idx}
            type="button"
            data-testid={`reviews-dot-${idx}`}
            onClick={() => setI(idx)}
            aria-label={`Go to review ${idx + 1}`}
            className={`h-[3px] transition-all ${idx === i ? "bg-[#D97645] w-10" : "bg-[#3A3229] hover:bg-[#B8A891] w-5"}`}
          />
        ))}
      </div>
    </div>
  );
}
